import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchArchdeaconries } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { MapPin, User, Church, ChevronRight, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Archdeaconry {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  parishes?: { id: number; name: string }[];
  current_archdeacon?: {
    id: number;
    title: string;
    name: string;
    photo?: string;
  };
  current_supervisor?: {
    id: number;
    title: string;
    name: string;
    photo?: string;
  };
}

const Archdeaconries = () => {
  const [archdeaconries, setArchdeaconries] = useState<Archdeaconry[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadArchdeaconries = async () => {
      try {
        const response = await fetchArchdeaconries();
        setArchdeaconries(response.data);
      } catch (error) {
        console.error('Error fetching archdeaconries:', error);
        setArchdeaconries([]); 
      } finally {
        setLoading(false);
      }
    };
    loadArchdeaconries();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
      <div className="animate-pulse font-serif italic text-amber-800">Organizing Diocesan Records...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <h2 className="text-amber-600 font-bold tracking-[0.2em] uppercase text-sm mb-3">Governance</h2>
          <h1 className="text-4xl lg:text-6xl font-serif font-bold text-slate-900 mb-6">
            Diocesan Archdeaconries
          </h1>
          <div className="h-1 w-24 bg-amber-600 mx-auto mb-6"></div>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto font-light leading-relaxed">
            The administrative pillars of the Diocese of Ibadan South, facilitating mission 
            and ministry across all pastoral areas.
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {archdeaconries.map((arch) => {
            const leader = arch.current_archdeacon || arch.current_supervisor;
            const isSupervisor = !arch.current_archdeacon && arch.current_supervisor;

            return (
              <Card key={arch.id} className="group border-none shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden bg-white ring-1 ring-slate-100">
                <div className="flex flex-col md:flex-row">
                  
                  {/* Left Side: Leader/Info */}
                  <div className={`md:w-1/3 p-6 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-slate-50 ${isSupervisor ? 'bg-slate-50' : 'bg-amber-50/30'}`}>
                    <div className="relative mb-4">
                      <div className={`w-24 h-24 rounded-full overflow-hidden ring-4 ${isSupervisor ? 'ring-slate-200' : 'ring-amber-200'}`}>
                        <img 
                          src={leader?.photo || "https://res.cloudinary.com/dwrqyrm84/image/upload/t_dp_logo/v1749389564/mq1jwwqt9xxehogbwlem.png"} 
                          alt={leader?.name} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
                      {isSupervisor ? 'Supervisor' : 'Archdeacon'}
                    </p>
                    <p className="text-center text-sm font-bold text-slate-800 leading-tight">
                      {leader ? `${leader.title} ${leader.name.split(' ').pop()}` : 'Vacant'}
                    </p>
                  </div>

                  {/* Right Side: Content */}
                  <div className="md:w-2/3 p-8 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <CardTitle className="text-2xl font-serif text-slate-900 group-hover:text-amber-700 transition-colors">
                          {arch.name}
                        </CardTitle>
                        <div className="bg-slate-900 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center">
                          <Church className="h-3 w-3 mr-1.5 text-amber-400" />
                          {arch.parishes?.length || 0}
                        </div>
                      </div>

                      {arch.description ? (
                        <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3">
                          {arch.description}
                        </p>
                      ) : (
                        <p className="text-slate-400 text-sm italic mb-6">
                          Dedicated to spiritual growth and community development within the {arch.name} jurisdiction.
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                      <div className="flex items-center text-slate-400 text-xs">
                        <MapPin className="h-3 w-3 mr-1" />
                        Ibadan South
                      </div>
                      <Link
                        to={`/archdeaconries/${arch.slug}`}
                        className="flex items-center text-amber-700 font-bold text-xs uppercase tracking-widest hover:text-slate-900 transition-colors"
                      >
                        Explore Parishes <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </div>
                  </div>

                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Archdeaconries;